import api from "@/shared/utils/api";
import { settings } from "@/shared/utils/settings";

export interface Organization {
  id: string;
  name: string;
  description?: string;
  uid: string;
  is_active: boolean;
  is_internal: boolean;
  created_at: string;
  next_payment_date: string;
  users_count: number;
  tariff: {
    name: string;
    uid: string;
    is_active: boolean;
    id?: string;
  };
}

export interface CreateOrganizationPayload {
  name: string;
  description: string;
  tariff_id: string;
  owner_name: string;
  owner_email: string;
  owner_password: string;
  is_internal: boolean;
}

export interface UpdateOrganizationPayload {
  name: string;
  description?: string;
  tariff_id: string;
  next_payment_date: string;
  is_active: boolean;
  is_internal: boolean;
}

export class OrganizationService {
  /**
   * Get all organizations
   * @returns Promise with all organizations
   */
  static async getAll(): Promise<Organization[]> {
    try {
      const response = await api.get(settings.api.admin.organizations);
      return response.data;
    } catch (error) {
      console.error("Failed to fetch organizations:", error);
      throw error;
    }
  }

  /**
   * Create a new organization
   * @param organizationData - Organization data to create
   * @returns Promise with the created organization
   */
  static async create(
    organizationData: CreateOrganizationPayload
  ): Promise<any> {
    try {
      const response = await api.post(
        settings.api.admin.organizations,
        organizationData
      );
      return response.data;
    } catch (error) {
      console.error("Failed to create organization:", error);
      throw error;
    }
  }

  /**
   * Delete an organization
   * @param id - Organization ID to delete
   */
  static async delete(id: string): Promise<any> {
    try {
      const response = await api.delete(
        `${settings.api.admin.organizations}/${id}`
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to delete organization with ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Activate an organization
   * @param id - Organization ID to activate
   */
  static async activate(id: string): Promise<any> {
    try {
      const response = await api.post(
        `${settings.api.admin.organizations}/${id}/activate`
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to activate organization with ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Update an organization
   * @param id - Organization ID to update
   * @param organizationData - Organization data to update
   * @returns Promise with the updated organization
   */
  static async update(
    id: string,
    organizationData: UpdateOrganizationPayload
  ): Promise<any> {
    try {
      const response = await api.put(
        `${settings.api.admin.organizations}/${id}`,
        organizationData
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to update organization with ID ${id}:`, error);
      throw error;
    }
  }
}
